import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import type { ExerciseFeedbackMessage } from '../../hooks/content-renderer-types';

export type ConversationProvider = 'openai' | 'elevenlabs' | 'gemini';

export type ConversationState =
  | 'idle'
  | 'connecting'
  | 'active'
  | 'ending'
  | 'completed'
  | 'viewing_history';

export interface ConversationalExerciseContent extends StudentExerciseContent {
  theme?: string;
  maxDurationSec?: number;
  systemPrompt?: string;
  targetSkills?: string[];
}

export interface ConversationalExerciseProps {
  content: ConversationalExerciseContent;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
  viewerStudentId?: string;
}

export interface ConversationTurn {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  /** OpenAI conversation item id, used to place the transcript in the right bubble. */
  itemId?: string;
  /** Audio was sent, transcript not back yet. */
  pending?: boolean;
  /** Transcription failed for this recording (the AI still heard it). */
  failed?: boolean;
}

export interface ConversationCorrection {
  studentQuote: string;
  correctedForm: string;
  habit: string;
  explanation: string;
}

export interface SessionTurn {
  turnId?: string;
  role?: string;
  content?: string;
  audioUrl?: string | null;
  timestamp?: Date | string;
  order?: number;
}

export interface SkillAssessment {
  skill?: string;
  score?: number;
  feedback?: string;
}

export interface SessionEvaluation {
  overallScore?: number;
  maxScore?: number;
  overallFeedback?: string;
  skillAssessments?: SkillAssessment[];
  corrections?: ConversationCorrection[];
  evaluator?: 'ai' | 'manual' | 'none';
  lowContent?: boolean;
  /** Finalised later from the saved turns (the call was interrupted). */
  recovered?: boolean;
}

export interface SessionSummary {
  sessionId?: string;
  status?: string;
  startedAt?: Date | string;
  endedAt?: Date | string | null;
  score?: number | null;
  maxScore?: number | null;
  evaluation?: SessionEvaluation | null;
}

export interface SessionWithTurns extends SessionSummary {
  turns?: SessionTurn[];
}

export interface CompletionResult {
  score: number;
  maxScore: number;
  passingScore?: number;
  passed?: boolean;
  feedback: string;
  skillAssessments: SkillAssessment[];
  corrections?: ConversationCorrection[];
  evaluator?: 'ai' | 'manual' | 'none';
  lowContent?: boolean;
  recovered?: boolean;
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
